import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { useState } from 'react'
import { LuMessageSquareHeart, LuStar } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'

import { getSavedAuthData } from '../../../api/axios/interceptor'

/**
 * Pilot satisfaction micro-survey. Shown once per user (server-tracked via
 * GET /api/feedback/status) after they've engaged with a few cases. Four quick
 * questions; only the NPS is required so it stays a <1 min ask. Submitting POSTs
 * to /api/feedback, which stores the row and emails the team.
 */
type TimeSaving = 'beaucoup' | 'un_peu' | 'pas_vraiment'

export default function FeedbackModal({
  isOpen,
  onClose,
  onSubmitted,
  context,
}: {
  isOpen: boolean
  /** Dismissed without submitting (e.g. "Plus tard") — caller snoozes. */
  onClose: () => void
  /** Submitted successfully — caller marks done so it never shows again. */
  onSubmitted: () => void
  /** Light metadata stored with the response (e.g. engaged case count). */
  context?: Record<string, unknown>
}) {
  const toast = useToast()
  const { t } = useTranslation()
  const [nps, setNps] = useState<number | null>(null)
  const [confidence, setConfidence] = useState<number | null>(null)
  const [timeSaving, setTimeSaving] = useState<TimeSaving | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const apiDomain = process.env.REACT_APP_API_DOMAIN || ''

  const submit = async () => {
    setSubmitting(true)
    try {
      const { accessToken } = getSavedAuthData()
      const res = await fetch(`${apiDomain}/api/feedback`, {
        method: 'POST',
        headers: { Authorization: accessToken || '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ npsScore: nps, confidence, timeSaving, comment, context }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast({ title: t('blotAnalysis.feedback.thanks'), status: 'success', duration: 4000 })
      onSubmitted()
    } catch (e) {
      toast({ title: t('blotAnalysis.common.sendFailed'), description: t('blotAnalysis.common.retryLater'), status: 'error', duration: 4000 })
    } finally {
      setSubmitting(false)
    }
  }

  const npsColor = (n: number) => (n <= 6 ? 'red' : n <= 8 ? 'orange' : 'green')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader p={0}>
          <Box bgGradient="linear(135deg, teal.500, cyan.500)" color="white" px={6} py={5}>
            <HStack spacing={3}>
              <Box bg="whiteAlpha.300" borderRadius="xl" p={2.5}>
                <Icon as={LuMessageSquareHeart} boxSize={6} />
              </Box>
              <Box>
                <Text fontSize="lg" fontWeight="bold" lineHeight="1.2">{t('blotAnalysis.feedback.title')}</Text>
                <Text fontSize="sm" color="whiteAlpha.900">{t('blotAnalysis.feedback.subtitle')}</Text>
              </Box>
            </HStack>
          </Box>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} right={4} />

        <ModalBody px={6} py={5}>
          <VStack align="stretch" spacing={6}>
            {/* NPS 0–10 */}
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={2}>
                Quelle est la probabilité que vous recommandiez Careflai à un confrère ?
              </Text>
              <HStack spacing={1} flexWrap="wrap">
                {Array.from({ length: 11 }, (_, n) => (
                  <Button
                    key={n}
                    size="sm"
                    minW="34px"
                    px={0}
                    variant={nps === n ? 'solid' : 'outline'}
                    colorScheme={nps === n ? npsColor(n) : 'gray'}
                    onClick={() => setNps(n)}
                  >
                    {n}
                  </Button>
                ))}
              </HStack>
              <HStack justify="space-between" mt={1}>
                <Text fontSize="2xs" color="gray.400">{t('blotAnalysis.feedback.notAtAll')}</Text>
                <Text fontSize="2xs" color="gray.400">{t('blotAnalysis.feedback.absolutely')}</Text>
              </HStack>
            </Box>

            {/* Confidence 1–5 */}
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={2}>
                Les suggestions de l'outil (bandes, témoin, interprétation) vous paraissent-elles fiables ?
              </Text>
              <HStack spacing={1}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Icon
                    key={n}
                    as={LuStar}
                    boxSize={7}
                    cursor="pointer"
                    color={confidence != null && n <= confidence ? 'yellow.400' : 'gray.300'}
                    fill={confidence != null && n <= confidence ? 'currentColor' : 'none'}
                    onClick={() => setConfidence(n)}
                    transition="color 0.1s"
                  />
                ))}
              </HStack>
            </Box>

            {/* Time saving */}
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={2}>
                L'outil vous fait-il gagner du temps par rapport à votre méthode habituelle ?
              </Text>
              <HStack spacing={2}>
                {([
                  ['beaucoup', t('blotAnalysis.feedback.aLot')],
                  ['un_peu', t('blotAnalysis.feedback.aLittle')],
                  ['pas_vraiment', t('blotAnalysis.feedback.notReally')],
                ] as Array<[TimeSaving, string]>).map(([val, label]) => (
                  <Button
                    key={val}
                    size="sm"
                    flex="1"
                    variant={timeSaving === val ? 'solid' : 'outline'}
                    colorScheme={timeSaving === val ? 'teal' : 'gray'}
                    onClick={() => setTimeSaving(val)}
                  >
                    {label}
                  </Button>
                ))}
              </HStack>
            </Box>

            {/* Open comment */}
            <Box>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800" mb={2}>
                {t('blotAnalysis.feedback.openQuestion')}
              </Text>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('blotAnalysis.feedback.commentPlaceholder')}
                rows={3}
                fontSize="sm"
                resize="vertical"
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
          <Button variant="ghost" color="gray.500" onClick={onClose} isDisabled={submitting}>
            Plus tard
          </Button>
          <Button
            colorScheme="teal"
            onClick={submit}
            isLoading={submitting}
            isDisabled={nps === null}
            borderRadius="lg"
          >
            Envoyer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
